import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';

const Footer: React.FC = () => {
  const footerLinks = {
    company: [
      { name: 'من نحن', href: '/about' },
      { name: 'فريق العمل', href: '/team' },
      { name: 'الوظائف', href: '/careers' },
      { name: 'الأخبار', href: '/news' },
    ],
    product: [
      { name: 'الميزات', href: '/features' },
      { name: 'الأسعار', href: '/pricing' },
      { name: 'API', href: '/api' },
      { name: 'التكاملات', href: '/integrations' },
    ],
    support: [
      { name: 'مركز المساعدة', href: '/help' },
      { name: 'الدعم الفني', href: '/support' },
      { name: 'الحالة', href: '/status' },
      { name: 'التحديثات', href: '/updates' },
    ],
    legal: [
      { name: 'الخصوصية', href: '/privacy' },
      { name: 'الشروط والأحكام', href: '/terms' },
      { name: 'الامتثال', href: '/compliance' },
      { name: 'الأمان', href: '/security' },
    ],
  };

  const socialLinks = [
    { name: 'LinkedIn', href: '#', icon: '💼' },
    { name: 'Twitter', href: '#', icon: '🐦' },
    { name: 'YouTube', href: '#', icon: '📺' },
    { name: 'WhatsApp', href: '#', icon: '💬' },
  ];

  const certifications = [
    { name: 'SAMA', description: 'مرخصة من ساما' },
    { name: 'MUDAD', description: 'متوافقة مع مدد' },
    { name: 'SSL', description: 'شهادة SSL' },
    { name: 'API', description: 'API جاهز' },
  ];

  return (
    <footer className="bg-gray-900 dark:bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-2 rtl:space-x-reverse mb-6">
                <motion.div
                  className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center"
                  whileHover={{ scale: 1.1, rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  <span className="text-white font-bold text-lg">F</span>
                </motion.div>
                <span className="text-2xl font-bold">FLYN</span>
              </Link>
              
              <p className="text-gray-400 mb-6 leading-relaxed">
                منصة الذكاء المالي الرائدة للشركات في المملكة العربية السعودية. 
                نحول بياناتك المالية إلى قرارات ذكية وفرص نمو حقيقية.
              </p>

              {/* Contact Info */}
              <div className="space-y-3">
                <div className="flex items-center space-x-3 rtl:space-x-reverse text-gray-400">
                  <EnvelopeIcon className="w-5 h-5" />
                  <span>info@flyn.sa</span>
                </div>
                <div className="flex items-center space-x-3 rtl:space-x-reverse text-gray-400">
                  <PhoneIcon className="w-5 h-5" />
                  <span>+966 11 123 4567</span>
                </div>
                <div className="flex items-center space-x-3 rtl:space-x-reverse text-gray-400">
                  <MapPinIcon className="w-5 h-5" />
                  <span>الرياض، المملكة العربية السعودية</span>
                </div>
              </div>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">الشركة</h3>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">المنتج</h3>
              <ul className="space-y-3">
                {footerLinks.product.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">الدعم</h3>
              <ul className="space-y-3">
                {footerLinks.support.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal Links */}
            <div>
              <h3 className="text-lg font-semibold mb-6">قانوني</h3>
              <ul className="space-y-3">
                {footerLinks.legal.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className="mt-16 pt-8 border-t border-gray-800">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-xl font-semibold mb-2">
                  اشترك في النشرة الإخبارية
                </h3>
                <p className="text-gray-400">
                  احصل على آخر التحديثات والنصائح المالية مباشرة في بريدك الإلكتروني
                </p>
              </div>
              <div className="flex space-x-3 rtl:space-x-reverse">
                <input
                  type="email"
                  placeholder="بريدك الإلكتروني"
                  className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-6 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition-all duration-200"
                >
                  اشتراك
                </motion.button>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="py-8 border-t border-gray-800">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
            {/* Copyright */}
            <div className="text-gray-400 text-sm">
              © 2025 FLYN Corporate. جميع الحقوق محفوظة.
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-6 rtl:space-x-reverse">
              {socialLinks.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-full flex items-center justify-center transition-colors duration-200"
                  aria-label={social.name}
                >
                  <span className="text-lg">{social.icon}</span>
                </motion.a>
              ))}
            </div>

            {/* Certifications */}
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              {certifications.map((cert) => (
                <div
                  key={cert.name}
                  className="flex items-center space-x-1 rtl:space-x-reverse text-xs text-gray-400"
                  title={cert.description}
                >
                  <div className="w-6 h-6 bg-gray-800 rounded flex items-center justify-center">
                    <span className="text-xs font-bold">{cert.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-6 pt-6 border-t border-gray-800 text-center">
            <p className="text-gray-500 text-xs">
              FLYN مرخصة من البنك المركزي السعودي (SAMA) ومتوافقة مع قانون حماية البيانات الشخصية (PDPL)
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
